import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/common/Logo/Logo';
import styles from './Footer.module.css';

/**
 * Footer – CRYPTMET theme
 * Structure: Logo → Grid (About + Social | Games, Casino, Legal, Trust & Support) → Divider → Accepted currencies → Note (incl. P2P)
 * F) Trust, Support & Security: Help Center, Live Chat, Security Center, KYC, Platform Status, Responsible Play, Notifications
 */

const socialLinks = [
  { src: '/assets/images/svg/facebook.svg', alt: 'Facebook', href: '', name: 'Facebook' },
  { src: '/assets/images/svg/insta.svg', alt: 'Instagram', href: 'http://instagram.com/', name: 'Instagram' },
  { src: '/assets/images/svg/telegram.svg', alt: 'Telegram', href: '', name: 'Telegram' },
];

const acceptedCurrencies = [
  { src: '/assets/images/coins/USDT.webp', alt: 'USDT', title: 'USDT' },
  { src: '/assets/images/coins/BTC.webp', alt: 'BTC', title: 'BTC' },
  { src: '/assets/images/coins/ETH.webp', alt: 'ETH', title: 'ETH' },
  { src: '/assets/images/coins/LTC.webp', alt: 'LTC', title: 'LTC' },
  { src: '/assets/images/coins/XRP.webp', alt: 'XRP', title: 'XRP' },
  { src: '/assets/images/coins/DOGE.webp', alt: 'DOGE', title: 'DOGE' },
  { src: '/assets/images/coins/BCH.webp', alt: 'BCH', title: 'BCH' },
  { src: '/assets/images/coins/ADA.webp', alt: 'ADA', title: 'ADA' },
  { src: '/assets/images/coins/BNB.webp', alt: 'BNB', title: 'BNB' },
];

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        {/* Logo */}
        <div className={styles.footerTop}>
          <Link to="/" className={styles.logoLink}>
            <Logo size="large" />
          </Link>
        </div>

        {/* Main grid: About + Social | Link columns */}
        <div className={styles.footerGrid}>
          {/* Left: About + Social */}
          <div className={styles.footerAbout}>
            <p className={styles.aboutText}>
              {t('footer.aboutDescription')}
            </p>
            <p className={styles.aboutLegal}>
              {t('footer.agreeTerms')}
            </p>
            <div className={styles.socialWrap}>
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialBtn}
                  aria-label={social.name}
                >
                  <img src={social.src} alt={social.alt} className={styles.socialBtnIcon} />
                </a>
              ))}
            </div>
          </div>

          {/* Right: Link columns */}
          <div className={styles.linkColumns}>
            <div className={styles.linkCol}>
              <h4 className={styles.linkColTitle}>{t('footer.games')}</h4>
              <Link to="/casino/popular-slots" className={styles.linkColItem}>{t('footer.slots')}</Link>
              <Link to="/casino/live-casino" className={styles.linkColItem}>{t('footer.liveCasino')}</Link>
              <Link to="/casino/crash-games" className={styles.linkColItem}>{t('footer.crash')}</Link>
              <Link to="/casino" className={styles.linkColItem}>{t('footer.casinoLobby')}</Link>
            </div>
            <div className={styles.linkCol}>
              <h4 className={styles.linkColTitle}>{t('footer.casino')}</h4>
              <Link to="/promotions" className={styles.linkColItem}>{t('nav.promotions')}</Link>
              <Link to="/affiliate" className={styles.linkColItem}>{t('footer.affiliateProgram')}</Link>
              <Link to="/vip-club" className={styles.linkColItem}>{t('nav.vipClub')}</Link>
              <Link to="/bonus-page" className={styles.linkColItem}>{t('footer.redeemRakeback')}</Link>
            </div>
            <div className={styles.linkCol}>
              <h4 className={styles.linkColTitle}>{t('footer.legal')}</h4>
              <Link to="/provably-fair?tab=Privacy+Policy" className={styles.linkColItem}>{t('footer.privacyPolicy')}</Link>
              <Link to="/provably-fair?tab=Terms+of+Service" className={styles.linkColItem}>{t('footer.termsConditions')}</Link>
              <Link to="/provably-fair?tab=User+Agreement" className={styles.linkColItem}>{t('footer.responsibleGaming')}</Link>
            </div>
            <div className={styles.linkCol}>
              <h4 className={styles.linkColTitle}>{t('footer.trustSupport')}</h4>
              <Link to="/provably-fair?tab=Provably+fair" className={styles.linkColItem}>{t('footer.helpCenter')}</Link>
              <Link to="/provably-fair?tab=FAQ" className={styles.linkColItem}>FAQ</Link>
              <Link to="/provably-fair?tab=Support" className={styles.linkColItem}>{t('footer.contactUs')}</Link>
              <Link to="/settings" className={styles.linkColItem}>{t('footer.kycVerification')}</Link>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.footerDivider} role="separator" />

        {/* Accepted currencies + bottom */}
        <div className={styles.footerBottom}>
          <div className={styles.currenciesBlock}>
            <span className={styles.currenciesLabel}>{t('common.acceptedCurrencies')}</span>
            <div className={styles.currenciesRow}>
              {acceptedCurrencies.map((c, i) => (
                <img
                  key={i}
                  src={c.src}
                  alt={c.alt}
                  title={c.title}
                  className={styles.currencyIcon}
                />
              ))}
            </div>
            <p className={styles.p2pNote}>
              {t('footer.p2pNote')}
            </p>
          </div>
        </div>

        {/* Note */}
        <div className={styles.noteSection}>
          <p className={styles.noteText}>
            {t('footer.ageDisclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
